import React, { useState } from 'react';
import Calculator from './Calculator';
import '../App.css';

// Tooltip component for cash-in explanation
const CashInTooltip = () => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span 
      className="relative inline-block ml-2"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="inline-flex items-center justify-center w-4 h-4 text-xs bg-[#A285D1] text-white rounded-full cursor-help hover:bg-[#4A1D7A] transition-colors duration-200">
        ?
      </span>
      {showTooltip && (
        <div className="absolute z-50 top-6 left-1/2 transform -translate-x-1/2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-xl">
          <div className="text-sm text-[#4A1D7A]">
            <h4 className="font-bold mb-2">What is "cashed-in"?</h4>
            <p className="mb-2">
              Cashed-in means you're ready to get your final qualification grade after completing all required units.
            </p>
            <p className="mb-2">
              Think of it like collecting puzzle pieces - cashing-in is when you have all the pieces and put them together for your final grade.
            </p>
            <p className="text-xs font-medium">
              <strong>Important:</strong> You need to cash-in to make your qualification official, even if you've passed all units.
            </p>
          </div>
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-white"></div>
        </div>
      )}
    </span>
  );
};

const CACHED_INFO = {
  type: 'info',
  key: 'cashedInfo',
  content: (
    <div className="text-[#4A1D7A]">
      <h3 className="font-bold text-lg mb-2">Cashed Units Guidance</h3>
      <p className="mb-2">If you have previously cashed any units, you may need to have the cash in removed before they can be used in a new qualification.</p>
      <a
        href="https://qualifications.pearson.com/content/dam/pdf/International%20Advanced%20Level/Mathematics/2018/Teaching-and-Learning-Materials/aggregation-rules-and-guidance.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0369A1] hover:underline font-semibold mb-2 inline-block"
      >
        View detailed aggregation guidance from the main calculator page after selection.
      </a>
    </div>
  )
};

const UNIT_GUIDE_INFO = {
  type: 'info',
  key: 'unitGuide',
  content: (
    <div className="text-[#4A1D7A]">
      <h3 className="font-bold text-lg mb-2">How to Use the Calculator</h3>
      <ul className="list-disc pl-5 mb-2">
        <li>Select units by clicking on the unit cards in each section (Pure, Further Pure, Applied).</li>
        <li>Selected units will appear in the floating panel at the bottom right.</li>
        <li>Once you have selected your units, click <span className="font-semibold">Check Eligibility</span> to see which qualifications you are eligible for.</li>
        <li>You can expand/collapse sections and deselect units at any time.</li>
        <li>For best results, ensure you have selected all units you wish to cash in for this exam series.</li>
      </ul>
      <p className="mt-2">If you need help, consult the official Pearson guidance or your examination officer.</p>
    </div>
  )
};

const TRANSFER_OF_CREDIT_INFO = {
  type: 'info',
  key: 'transferOfCreditInfo',
  content: (
    <div className="text-[#4A1D7A]">
      <h3 className="font-bold text-lg mb-2">Transfer of Credit Information</h3>
      <p className="mb-2">
        If you are cashing in Edexcel IAL Mathematics with units from another exam board, you'll need to follow the Transfer of Credit process.
      </p>
      <a
        href="https://qualifications.pearson.com/en/support/support-topics/exams/special-requirements/transfer-of-credit.html"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0369A1] hover:underline font-semibold mb-2 inline-block"
      >
        Visit the Transfer of Credit page &rarr;
      </a>
      <h4 className="font-semibold mt-4 mb-1">Transfer of Credit Key Points:</h4>
      <ul className="list-disc pl-5 text-sm mb-2">
        <li>You must apply for Transfer of Credit before the qualification can be awarded</li>
        <li>Applications must be made via your examination officer</li>
        <li>Evidence of the previously achieved qualification must be provided</li>
        <li>Check deadline dates carefully - applications must be received before results day</li>
      </ul>
      <p className="mt-2 text-sm text-[#4A1D7A]">If you are not able to proceed, you may close the app below.</p>
    </div>
  )
};

const UCI_MERGE_INFO = {
  type: 'info',
  key: 'uciMergeInfo',
  content: (
    <div className="text-[#4A1D7A]">
      <h3 className="font-bold text-lg mb-2">UCI Merge Required</h3>
      <p className="mb-2">
        The candidate's UCI number and personal details must be the same across all exam series to ensure proper unit aggregation and award eligibility.
      </p>
      <p className="mb-2">
        If these details are not consistent, you will need to request a UCI merge before proceeding. This process ensures all results are correctly linked to the candidate.
      </p>
      <a
        href="https://support.pearson.com/uk/s/article/UCI-Numbers"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0369A1] hover:underline font-semibold mb-2 inline-block"
      >
        Request a UCI Merge &rarr;
      </a>
      <p className="mt-2 text-sm text-[#4A1D7A]">If you are not able to proceed, you may close the app below.</p>
    </div>
  )
};

function GuidedCalculator() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [result, setResult] = useState(null);
  const [expandStates, setExpandStates] = useState({ pure: false, further: false, applied: false });

  // Dynamically build steps based on answers
  let dynamicSteps = [
    {
      question: "Can you confirm the candidate's UCI number and personal details are the same across all series?",
      key: "uciConfirmed",
      options: ["Yes", "No"]
    },
    {
      question: (
        <span>
          Have you used any units that were previously cashed?
          <CashInTooltip />
        </span>
      ),
      key: "unCashedUnits",
      options: ["Yes", "No"]
    },
    {
      question: "Are you cashing in Edexcel IAL Mathematics with units from another exam board?",
      key: "transferOfCredit",
      options: ["Yes", "No"]
    },
    {
      question: "Are you cashing in a single code or multiple codes?",
      key: "cashInType",
      options: ["Single code", "Multiple codes"]
    }
  ];

  // Insert UCI merge info step if user answered 'No' to UCI confirmation
  if (answers.uciConfirmed === 'No') {
    dynamicSteps.splice(1, 0, UCI_MERGE_INFO);
  }
  // Insert transfer of credit info step after transferOfCredit if user answered 'Yes'
  if (answers.transferOfCredit === 'Yes') {
    dynamicSteps.splice(4, 0, TRANSFER_OF_CREDIT_INFO);
  }
  // Insert cashed info step if user answered 'Yes' to unCashedUnits
  if (answers.unCashedUnits === 'Yes') {
    dynamicSteps.splice(dynamicSteps.findIndex(s => s.key === 'transferOfCredit'), 0, CACHED_INFO);
  }
  // Move the mode selection question to the last position before the unit guide info
  const modeStepIndex = dynamicSteps.findIndex(s => s.key === 'cashInType');
  if (modeStepIndex !== -1) {
    const [modeStep] = dynamicSteps.splice(modeStepIndex, 1);
    // Insert before the last info step (UNIT_GUIDE_INFO will be pushed after this)
    dynamicSteps.push(modeStep);
  }
  // Always add the unit guide step before launching the calculator
  dynamicSteps.push(UNIT_GUIDE_INFO);

  const current = dynamicSteps[step];

  const handleOptionSelect = (option) => {
    setAnswers({ ...answers, [current.key]: option });
    if (step < dynamicSteps.length - 1) {
      setStep(step + 1);
    } else {
      setShowCalculator(true);
    }
  };

  const handleContinue = () => {
    if (step < dynamicSteps.length - 1) {
      setStep(step + 1);
    } else {
      setShowCalculator(true);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const resetApp = () => {
    setSelectedUnits([]);
    setResult(null);
    setExpandStates({ pure: false, further: false, applied: false });
    setStep(0);
    setShowCalculator(false);
    setAnswers({});
  };

  if (showCalculator) {
    return (
      <Calculator
        selectedUnits={selectedUnits}
        setSelectedUnits={setSelectedUnits}
        result={result}
        setResult={setResult}
        expandStates={expandStates}
        setExpandStates={setExpandStates}
        onStartOver={resetApp}
        guidedContext={answers}
      />
    );
  }

  return (
    <div className="guided-calculator-container font-plus-jakarta flex flex-col items-center px-4 py-4">
      <div className="w-full max-w-2xl">
        {/* Header Card */}
        <div className="modern-card slide-up mb-6">
          <div className="p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#4A1D7A] to-[#5F259F] rounded-lg flex items-center justify-center mr-4">
                <img src="/img/PEARSON_LOGO_WHITE_RGB.svg" alt="Pearson Logo" className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#4A1D7A]">IAL Mathematics Eligibility Calculator</h1>
                <p className="text-grey-600 text-sm mt-1">Let's guide you through the qualification process</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#4A1D7A]">Step {step + 1} of {dynamicSteps.length}</span>
                <span className="text-sm text-grey-500">{Math.round(((step + 1) / dynamicSteps.length) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-grey-200 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-[#00B2A9] to-[#008F88] h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${((step + 1) / dynamicSteps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="modern-card slide-up">
          <div className="p-8">
            {current.type === 'info' ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-1">
                  {current.content}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-[#4A1D7A] mb-4">{current.question}</h2>
                </div>
                <div className="grid gap-3 max-w-md mx-auto">
                  {current.options.map((option, index) => (
                    <button
                      key={option}
                      onClick={() => handleOptionSelect(option)}
                      className="group relative py-4 px-6 rounded-lg bg-gradient-to-r from-[#A285D1] to-[#9B7FCB] text-white font-semibold hover:from-[#4A1D7A] hover:to-[#5F259F] focus:outline-none focus:ring-2 focus:ring-[#4A1D7A] focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
                      aria-label={option}
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <span className="relative z-10">{option}</span>
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-white/10 to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Navigation */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-grey-200">
              {step > 0 && (
                <button
                  onClick={handleBack}
                  className="btn-secondary flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
              )}

              <div className="flex gap-3">
                {current.type === 'info' && (current.key === 'uciMergeInfo' || current.key === 'transferOfCreditInfo') ? (
                  <>
                    <button
                      onClick={handleContinue}
                      className="btn-primary"
                    >
                      Continue
                    </button>
                    <button
                      onClick={resetApp}
                      className="btn-secondary"
                    >
                      Close App
                    </button>
                  </>
                ) : current.type === 'info' ? (
                  <button
                    onClick={handleContinue}
                    className="btn-primary flex items-center"
                  >
                    Continue
                    <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <div className="text-sm text-grey-500 italic">
                    Choose an option to continue
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-grey-500">
            Pearson - The world's lifelong learning company
          </p>
        </div>
      </div>
    </div>
  );
}

export default GuidedCalculator; 
